import { zodResolver } from "@hookform/resolvers/zod";
import { Card } from "@material-tailwind/react";
import { useQueryClient } from "@tanstack/react-query";
import { TFunction } from "i18next";
import { useContext } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { ApiError } from "~/src/api/types";
import AlertDefault, { AlertVariant } from "~/src/components/Alerts/AlertDefault";
import Text, { Header, TextVariant } from "~/src/components/Text/Text";
import UIContext from "~/src/contexts/UIContext/UIProvider";
import { useDialog } from "~/src/hooks/useDialog";
import LdirLogo from "../../assets/img/ldir-logo.svg";
import ButtonDefault, { ButtonVariant } from "../../components/Button/ButtonDefault";
import Input from "../../components/Form/Input/Input";
import AuthContext, { Credentials } from "../../contexts/AuthContext/AuthProvider";
import getAppInfo from "../../helpers/getAppInfo";
import { getEnvironmentLabel } from "~/src/appConfig";
import { useStateful } from "../../hooks/useStateful";

export const loginErrorMessage = (e: ApiError, t: TFunction) => {
  let title = t("actions.error");
  let message = e.message || t("errors.unexpectedError", { details: t("errors.tryAgainLater") });

  if (["MissingAuthData"].includes(e.message)) {
  } else if (e.message === "InvalidCredentials") {
    title = t("pages.login.validation.invalidCredentials.title");
    message = t("pages.login.validation.invalidCredentials");
  }

  return { message: message, title: title };
};

export default function Login(): JSX.Element {
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const isLoading = useStateful(false);
  const { t } = useTranslation();
  const appInfo = getAppInfo();
  const loginErrorDialog = useDialog();
  const isUgyldigInfo = useStateful(false);
  const queryClient = useQueryClient();
  const ui = useContext(UIContext);

  const onlyNumbers = new RegExp(/^[0-9]*$/);
  const loginValidation = z
    .object({
      Jegernummer: z
        .string({ error: t("form.fields.jegernummer.validationError") })
        .min(1, { error: t("form.fields.jegernummer.validationError") })
        .max(8, { error: t("form.fields.jegernummer.validationError") })
        .regex(onlyNumbers, t("form.errors.shouldBeNumber")),
      Fodselsdato: z
        .string({ error: t("form.fields.fodselsdato.validationError") })
        .min(6, { error: t("form.fields.fodselsdato.validationError") })
        .max(6, { error: t("form.fields.fodselsdato.validationError") })
        .regex(onlyNumbers, t("form.errors.shouldBeNumber")),
    })
    .refine(
      (data) => {
        return parseInt(data.Jegernummer) > 0;
      },
      {
        path: ["Jegernummer"],
        message: t("form.fields.jegernummer.validationError"),
      },
    );

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm<Credentials>({
    resolver: zodResolver(loginValidation),
    mode: "onBlur",
  });

  const onSubmit: SubmitHandler<Credentials> = async (credentials) => {
    isUgyldigInfo.set(false);
    isLoading.set(true);
    auth
      .handleLogin(credentials)
      .then(() => {
        queryClient.clear();
        navigate("/oversikt");
      })
      .catch((e: ApiError) => {
        // biome-ignore lint: må bruke any her for å få tak i errors
        const errorData: any = e.axiosError?.response?.data;
        if (errorData && "errors" in errorData) {
          if (errorData.errors && "Jegernummer" in errorData.errors) {
            setError("Jegernummer", e);
          }
          if (errorData.errors && "Fodselsdato" in errorData.errors) {
            setError("Fodselsdato", e);
          }
        } else {
          loginErrorDialog.show(loginErrorMessage(e, t));
        }
      })
      .finally(() => isLoading.set(false));
  };

  const globalNotifications = ui.getGlobalNotifications();

  return (
    <Card
      className={`bg-ldir-blue-900 h-screen rounded-none min-h-fit text-white shadow-xl`}
      shadow={false}
    >
      {globalNotifications && (
        <div className="flex flex-col p-2 gap-2 absolute w-full">
          {globalNotifications.map((notification) => (
            <AlertDefault canClose variant={AlertVariant.Warning} key={notification.message}>
              {notification.message}
            </AlertDefault>
          ))}
        </div>
      )}
      <div className="flex justify-between h-full min-h-fit flex-col w-full px-6">
        <div className="flex flex-col items-center justify-center grow mb-16 mt-4 flex-wrap">
          <Text
            variant={TextVariant.xlarge}
            className="font-serif font-extrabold bg-ldir-brown-300 text-ldir-blue-900 px-2 mb-4 w-full text-center"
          >
            {getEnvironmentLabel()?.toUpperCase()}
          </Text>
          <Header headerLvl={1} variant={TextVariant.xxlarge} serif>
            {t("app.name")}
          </Header>
          <dl className="text-center mt-4">
            <dt>{t("pages.login.versjon")}</dt>
            <dd className="mb-14">{appInfo.version}</dd>
          </dl>
          <form className="flex-col flex w-full" onSubmit={handleSubmit(onSubmit)}>
            <Input
              isOnDarkBg={true}
              id="loginPageJegernummerInput"
              label={t("form.fields.jegernummer.label")}
              placeholder="1-8 siffer"
              type="string"
              inputMode="numeric"
              step={1}
              errorMessage={errors.Jegernummer?.message}
              helpText={t("form.fields.jegernummer.helpText")}
              maxLength={8}
              {...register("Jegernummer")}
              onChange={(e) => {
                setError("Jegernummer", {});
                setValue("Jegernummer", e.target.value);
              }}
              className="mb-4"
            />
            <Input
              isOnDarkBg={true}
              id="loginPagePasswordInput"
              label={t("form.fields.fodselsdato.label")}
              type="string"
              inputMode="numeric"
              placeholder="ddmmåå"
              step={1}
              errorMessage={errors.Fodselsdato?.message}
              helpText={t("form.fields.fodselsdato.helpText")}
              maxLength={6}
              {...register("Fodselsdato")}
              onChange={(e) => {
                setError("Fodselsdato", {});
                setValue("Fodselsdato", e.target.value);
              }}
              className="mb-14"
            />
            <ButtonDefault
              id="loginPageSubmitButton"
              type="submit"
              fullWidth={true}
              variant={ButtonVariant.PrimaryDarkBg}
              loading={isLoading.value}
              label={t("actions.login")}
            />
          </form>
        </div>
        <div className="w-full">
          <img
            src={LdirLogo}
            className="mb-6 w-[1/2] min-w-32"
            alt="Lanbruksdirektoratet Eanandoallodirektoráhtta logo"
          />
        </div>
      </div>
      {loginErrorDialog.element}
    </Card>
  );
}
